import { Gift, CaretRight, Trash, X } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import SwitchCouponDialog from "components/checkout/dialogs/SwitchCouponDialog";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import AddCouponDialog from "components/payments/methods/GyftrDialogs/AddGyftrVoucherDialog";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { PaymentMode } from "lib/types/payments";
import { deleteRequest, postRequest, staticOptions } from "lib/core/apiClient";
import { useLocale } from "lib/hooks/useLocale";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { apiURI, IMAGE_CDN_URL } from "lib/utils/constants";
import { currencyFormatter } from "lib/utils/formatters";
import { getInvalidDiscountCodes } from "lib/utils/rewards";
import { errorToast } from "lib/utils/toasters";
import React, { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";

const Gyftr = ({ dividerColor }: { dividerColor?: string }) => {
  const { t } = useLocale();

  const {
    state: { checkoutModal, checkoutId, giftCards },
    actions: { setCheckoutModal, updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: { paymentMethods },
    actions: { mutatePayment },
  } = usePaymentContext();

  const [modalType, setModalType] = useState<"ADD" | "REDEEM">("ADD");

  const hasGyftrMode = useMemo(
    () => paymentMethods?.find((e: PaymentMode) => e.name === "GYFTR"),
    [paymentMethods],
  );

  const shouldShowGyftrMode = useMemo(() => {
    if (!hasGyftrMode) return false;
    if (hasGyftrMode?.isAvailable) return true;
    return hasGyftrMode?.unavailabilityReasons?.some((reason: any) => reason?.type === "INVALID_DISCOUNTS");
  }, [hasGyftrMode]);

  const { invalidDiscountCodeTitles, invalidDiscountCodes } = getInvalidDiscountCodes(
    hasGyftrMode?.unavailabilityReasons ?? [],
  );

  const shouldFetchGyftrBalance = checkoutId && isAuthenticated && hasGyftrMode;

  const { data: balance } = useSWR(
    () => (shouldFetchGyftrBalance ? `gc/voucher/balance` : null),
    async (url: string) => {
      const response: any = await postRequest(
        `/checkout/v2/checkout/${checkoutId}/${url}`,
        { voucher_payment_mode: "GYFTR" },
        "CHECKOUT",
      );
      return response?.balance ?? 0;
    },
    staticOptions,
  );

  const gyftrBalance = Number(parseFloat(balance || "0").toFixed(2));
  const redeemedGyftrAmount =
    giftCards?.find((giftCard) => giftCard?.gift_card_type === "GYFTR")?.amount || 0;

  const handleGyftrDialogContent = () => {
    setCheckoutModal("GYFTR_ADD_COUPON_DIALOG");
    if (gyftrBalance > 0) {
      setModalType("REDEEM");
      return;
    }
    setModalType("ADD");
  };

  const handleRemoveGiftCard = async (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    try {
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
      const response: any = await deleteRequest(`/checkout/v2/checkout/${checkoutId}/gc/voucher`, {
        voucher_payment_mode: "GYFTR",
        redemption_amount: redeemedGyftrAmount,
      });
      updateCheckoutBasedOnCheckoutResponse(response);
      mutate(DISCOUNT_PATH);
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
    } catch (error: any) {
      errorToast(error?.message);
      console.error(error?.message);
    }
  };

  if (!Boolean(paymentMethods) || !shouldShowGyftrMode || !isAuthenticated) return <></>;

  const divider = dividerColor ? <div className="h-px mx-3" style={{ backgroundColor: dividerColor }} /> : null;

  const renderRedeemedGyftrTile = () => {
    if (redeemedGyftrAmount > 0) {
      return (
        <>
          <div className="text-xs font-medium flex items-center gap-1.5">
            <img src={`${IMAGE_CDN_URL}/gyftr.png`} alt="" className="h-4 w-4" />
            <p className="self-end">{t("e_pay_voucher_applied")}</p>
          </div>
          <p className="text-xs font-medium flex items-center gap-1.5">
            <p>-{currencyFormatter(redeemedGyftrAmount ?? 0)}</p>
            <span onClick={handleRemoveGiftCard}>
              <div className="flex h-3.5 w-3.5 items-center justify-center">
                <Icon IconComponent={X} size="xxxs" iconProps={{ weight: "bold" }} />
              </div>
            </span>
          </p>
        </>
      );
    }

    if (balance > 0) {
      return (
        <>
          <div className="text-xs font-medium flex items-center gap-1.5">
            <img src={`${IMAGE_CDN_URL}/gyftr.png`} alt="" className="h-4 w-4" />
            <p className="self-end">{t("use_epay")}</p>
          </div>
          <p className="text-xs font-medium flex items-center gap-1.5">
            <p>
              <span className="font-semibold">{currencyFormatter(gyftrBalance ?? 0)}</span>
              {t("gyftr_balance")}
            </p>
            <div className="flex h-3.5 w-3.5 items-center justify-center">
              <Icon
                IconComponent={CaretRight}
                size="xxxs"
                className="text-zinc-700/60"
                iconProps={{ weight: "bold" }}
              />
            </div>
          </p>
        </>
      );
    }

    return (
      <>
        <div className="text-xs font-medium flex items-center gap-1.5">
          <img src={`${IMAGE_CDN_URL}/gyftr.png`} alt="" className="h-4 w-4" />
          <p className="self-end">{t("use_epay")}</p>
        </div>
        <p className="text-xs font-medium flex items-center">
          <div className="flex h-3.5 w-3.5 items-center justify-center">
            <Icon
              IconComponent={CaretRight}
              size="xxs"
              className="text-zinc-700/60"
              iconProps={{ weight: "bold" }}
            />
          </div>
        </p>
      </>
    );
  };

  return (
    <>{divider}
      <div
        className="flex justify-between w-full py-2 px-3 cursor-pointer"
        onClick={(e) => {
          handleGyftrDialogContent();
        }}>
        {renderRedeemedGyftrTile()}
      </div>
      <GenericDialog
        isOpen={checkoutModal === "GYFTR_ADD_COUPON_DIALOG"}
        translateAxis="y"
        dialogOverlay={true}
        modalType="GYFTR_ADD_COUPON_DIALOG"
        customClass="overflow-scroll md:!top-auto md:absolute">
        <AddCouponDialog gyftrBalance={gyftrBalance || 0} modalType={modalType} setModalType={setModalType} />
      </GenericDialog>

      <GenericDialog
        isOpen={checkoutModal === "GYFTR_SWITCH_COUPON_DIALOG"}
        translateAxis="y"
        dialogOverlay={true}
        modalType="GYFTR_SWITCH_COUPON_DIALOG"
        customClass="overflow-scroll md:!top-auto md:absolute">
        <SwitchCouponDialog
          closePopup={() => setCheckoutModal("NONE")}
          applyCoupon={async () => {
            try {
              setCheckoutModal("LOADER");
              const promises = invalidDiscountCodes?.map((code: string) =>
                deleteRequest(`/checkout/v1/checkout/${checkoutId}/discounts`, { discount_code: code }),
              );
              const responses = await Promise.allSettled(promises);
              let replacedSuccessfully = true;
              let lastResponse;

              responses.forEach((response) => {
                if (response?.status !== "fulfilled") {
                  replacedSuccessfully = false;
                } else {
                  lastResponse = response.value;
                }
              });

              if (!replacedSuccessfully) {
                errorToast(t("failed_to_replace_coupons"));
                return;
              }

              updateCheckoutBasedOnCheckoutResponse(lastResponse);
              const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
              mutate(DISCOUNT_PATH);
              mutatePayment();
              mutate(`/checkout/${checkoutId}/rewards`);
            } catch (error) {
              console.error(error);
              errorToast(t("failed_to_replace_coupons"));
            } finally {
              setCheckoutModal("GYFTR_REDEEM_VOUCHER_DIALOG");
            }
          }}
          appliedDiscountCode={"GYFTR"}
          invalidDiscountCodes={invalidDiscountCodeTitles ?? []}
          invalidReason={"REWARDS_NOT_COMBINABLE"}
        />
      </GenericDialog>
    </>
  );
};

export default Gyftr;
